import React, { useState } from "react";
import { LinearGradient } from "expo-linear-gradient";
import { sumBy, get, findIndex, update } from "lodash/fp";
import { useQuery, gql } from "@apollo/client";
import { View, SectionList } from "react-native";
import { Feather } from "@expo/vector-icons";
import { ActivityIndicator, Text, TouchableRipple } from "react-native-paper";

import ChipPoint, { StatusText } from "../../components/ChipPoint";
import { ScreenProps, SubmissionStatus } from "../types";
import { CURRENT_MEMBER_FRAGMENT, MY_ACTIVITY_FRAGMENT, MyActivity as Activity } from "../fragments";
import { isCancelledOrExpired } from "./utils";
import { Dropdown, LinkButton, ScreenContainer } from "../../components";
import { planStartYear } from "../utils";
import { colors } from "../../../theme";
import config from "../../config";
import { useIntercomClient } from "../../hooks/useIntercom";

export const PLANS_QUERY = gql`
  query MyActivityHistoryPlans {
    plans: deferredCompPlans {
      id
      current
      startsOn
      confirmedPoints
    }
  }
`;

export const PLAN_QUERY = gql`
  query MyActivityHistory($planId: ID) {
    currentMember {
      myActivities: deferredCompSubmissions(planId: $planId) {
        ...MyActivityFragment
      }
      ...CurrentMemberFragment
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
  ${MY_ACTIVITY_FRAGMENT}
`;

interface Plan {
  id: string;
  startsOn: string;
  current: boolean;
  confirmedPoints: number;
}

export interface PlansQuery {
  plans: Plan[];
}

export interface Query {
  currentMember: { myActivities: Array<Activity> };
}

interface Section {
  title: string;
  predicate(a: Activity): boolean;
  alertHeader?: boolean;
  data: Array<Activity>;
}

const RecentActivityRow = ({ item, onPress }: { item: Activity; onPress(): void }) => {
  const { status, expiresSoon, expirationDate, participationLevel } = item;
  const {
    points,
    activity: { name },
  } = participationLevel;

  return (
    <TouchableRipple onPress={onPress} className="max-w-3xl mx-auto w-full">
      <View className="flex-row justify-between px-6 my-3">
        <View className="w-3/4">
          <Text className="text-regular-tight text-ink-darkest font-400">{name}</Text>
          <StatusText {...{ status, expiresSoon, expirationDate }} />
        </View>
        {!isCancelledOrExpired(status) && (
          <View>
            <ChipPoint {...{ points, status, expiresSoon }} />
          </View>
        )}
      </View>
    </TouchableRipple>
  );
};

const SectionHeader = ({ section }: { section: Section }) => {
  const { title, data, alertHeader } = section;
  const summedPoints = sumBy(get("participationLevel.points"))(data);
  const status = get("0.status")(data);

  const [containerClass, textClass] = alertHeader
    ? ["bg-red-lightest border-y border-red-lighter", "text-red-dark"]
    : ["bg-sky-lightest border-y border-sky", "text-ink-dark"];

  return (
    <View className={`${containerClass} py-3 px-6 flex-row justify-between max-w-3xl mx-auto w-full`}>
      <Text className={`text-small-tight font-700 ${textClass}`}>{title}</Text>
      {!isCancelledOrExpired(status) && (
        <Text className={`text-small-tight font-700 ${textClass}`} testID="summed-points">
          {summedPoints} pts
        </Text>
      )}
    </View>
  );
};

const sections: Array<Section> = [
  {
    title: "Attention Required",
    predicate: ({ expiresSoon, status }) => expiresSoon || status === SubmissionStatus.REJECTED,
    alertHeader: true,
    data: [],
  },
  {
    title: "Pending",
    predicate: ({ status }) => status === SubmissionStatus.PENDING,
    data: [],
  },
  {
    title: "Confirmed",
    predicate: ({ status }) => status === SubmissionStatus.APPROVED,
    data: [],
  },
  {
    title: "Canceled/Expired",
    predicate: ({ status }) => isCancelledOrExpired(status),
    data: [],
  },
];

export function makeSections(list: Array<Activity>) {
  function reduce(acc: Array<Section>, activity: Activity) {
    const groupIndex = findIndex(({ predicate }) => predicate(activity), acc);
    if (groupIndex === -1) return acc;
    return update(groupIndex, (group) => ({ ...group, data: group.data.concat(activity) }), acc);
  }

  return list.reduce(reduce, sections).filter(({ data }) => data.length > 0);
}

export default function MyActivityHistoryScreen({ navigation, route }: ScreenProps<"MyActivityHistory">) {
  const intercom = useIntercomClient();
  const navigateToMyActivity = (id: string, name: string) => {
    navigation.navigate("MyActivity", { id, name });
  };

  const [selectedPlan, setSelectedPlan] = useState<Plan>();

  const { loading: plansLoading, data: plansData } = useQuery<PlansQuery>(PLANS_QUERY, {
    onCompleted: (data) => {
      if (!selectedPlan && data?.plans) {
        setSelectedPlan(data.plans.find((p) => (route.params?.plan ? p.id === route.params.plan : p.current)));
      }
    },
  });
  const { data, loading: submissionsLoading } = useQuery<Query>(PLAN_QUERY, {
    skip: !selectedPlan,
    variables: { planId: selectedPlan?.id },
  });

  if (plansLoading) return <ActivityIndicator className="items-center justify-center flex-1" />;

  const plans = plansData?.plans || [];
  const sections = makeSections(data?.currentMember?.myActivities || []);

  return (
    <ScreenContainer navigationTitle="My Activities">
      <SectionList
        ListHeaderComponent={() => (
          <View className="sm:mt-6">
            <LinearGradient className="absolute w-full h-full" colors={[colors.sky.white, colors.sky.lightest]} />
            <View className="w-full max-w-3xl mx-auto">
              <View className="w-40 m-4">
                <Dropdown
                  setValue={(planId) => setSelectedPlan(plans.find((p) => p.id === planId))}
                  value={selectedPlan?.id}
                  list={plans.map((p) => ({ label: `${planStartYear(p)} Plan`, value: p.id }))}
                  inputProps={{ outlineStyle: { borderRadius: 14, borderColor: colors.ink.DEFAULT } }}
                  dense
                />
              </View>
              {selectedPlan && (
                <View className="flex-row mt-4 mx-6 mb-8">
                  <View className="flex-1 justify-between" testID="points-earned">
                    <Text className="text-tiny font-500">{planStartYear(selectedPlan)} Plan Points Earned</Text>
                    <Text className="text-title-2 font-700 text-ink-darker mt-2">{selectedPlan.confirmedPoints}</Text>
                  </View>
                  <View className="flex-1 pl-6 justify-between border-l-[1px] border-ink-lighter">
                    <Text className="text-tiny font-500">Plan Funds</Text>
                    <View className="flex-row gap-2">
                      <Feather name="info" color={colors.primary.DEFAULT} size={24} />
                      <LinkButton onPress={() => intercom.present(config.intercomContent.deferredCompPlanFundsInformation)}>
                        More Information
                      </LinkButton>
                    </View>
                  </View>
                </View>
              )}
            </View>
          </View>
        )}
        ItemSeparatorComponent={() => <View className="w-full max-w-3xl mx-auto border-t border-sky-light" />}
        sections={sections}
        keyExtractor={get("id")}
        renderItem={({ item }) => (
          <RecentActivityRow item={item} onPress={() => navigateToMyActivity(item.id, item.participationLevel.activity.name)} />
        )}
        renderSectionHeader={(props) => <SectionHeader {...props} />}
        contentContainerStyle={{ flexGrow: 1 }}
        ListEmptyComponent={
          <View className="items-center web:pt-20 my-auto pb-20" testID="empty-state">
            {submissionsLoading && !data && <ActivityIndicator />}
            {!submissionsLoading && data?.currentMember?.myActivities?.length === 0 && (
              <>
                <Text className="text-large-normal font-700">No activity submissions</Text>
                {selectedPlan && (
                  <Text className="text-regular-tight font-400 text-center px-12 mt-1">
                    No activities submitted for the {planStartYear(selectedPlan)} Plan Year.
                  </Text>
                )}
              </>
            )}
          </View>
        }
        stickySectionHeadersEnabled
      />
    </ScreenContainer>
  );
}
